import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../UI/Modal";
import { closeFormModal, openViewModal } from "../../store/reducers/schools/schools.reducer";
import { useEffect, useState } from "react";
import {
  createSchoolThunk,
  getSchoolsThunk,
  updateSchoolThunk,
  getSchoolByIdThunk,
} from "../../store/reducers/schools/schools.thunks";
import Loader from "../UI/Loader";
import Alert from "../UI/Alert";
import { toast } from "react-toastify";

const SchoolFormModal = () => {
  const dispatch = useDispatch();
  const { formModalOpen, formMode, selectedSchool, formLoading, formError, page, search } =
    useSelector((state) => state.schools);

  const [formData, setFormData] = useState({
    nombre: "",
    siglas: "",
    tipo: "",
    publica: true,
    para_publico_en_general: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (formMode === "edit" && selectedSchool) {
      setFormData({
        nombre: selectedSchool.nombre,
        siglas: selectedSchool.siglas,
        tipo: selectedSchool.tipo,
        publica: selectedSchool.publica,
        para_publico_en_general: selectedSchool.para_publico_en_general ?? false,
      });
    } else {
      setFormData({ nombre: "", siglas: "", tipo: "", publica: true, para_publico_en_general: false });
    }
    setErrors({});
  }, [formMode, selectedSchool, formModalOpen]);

  const handleClose = () => {
    dispatch(closeFormModal());
    setFormData({ nombre: "", siglas: "", tipo: "", publica: true, para_publico_en_general: false });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = "El nombre es requerido";
    if (!formData.siglas.trim()) newErrors.siglas = "Las siglas son requeridas";
    if (!formData.tipo.trim()) newErrors.tipo = "El tipo es requerido";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      nombre: formData.nombre.trim(),
      siglas: formData.siglas.trim(),
      tipo: formData.tipo.trim(),
      publica: formData.publica,
      para_publico_en_general: formData.para_publico_en_general,
    };

    let result;
    let recordId;

    if (formMode === "create") {
      result = await dispatch(createSchoolThunk(payload));
      if (result.meta.requestStatus === "fulfilled") {
        recordId = result.payload.data.id;
        toast.success("Escuela creada exitosamente");
      }
    } else {
      result = await dispatch(
        updateSchoolThunk({ id: selectedSchool.id, payload }),
      );
      if (result.meta.requestStatus === "fulfilled") {
        recordId = selectedSchool.id;
        toast.success("Escuela actualizada exitosamente");
      }
    }

    if (result.meta.requestStatus === "fulfilled") {
      dispatch(getSchoolsThunk({ page: formMode === "create" ? 1 : page, search }));
      handleClose();
      await dispatch(getSchoolByIdThunk(recordId));
      dispatch(openViewModal());
    }
  };

  return (
    <Modal
      open={formModalOpen}
      onClose={handleClose}
      title={formMode === "create" ? "Crear nueva escuela" : "Editar escuela"}
      size="xl"
    >
      <form onSubmit={handleSubmit}>
        {formError && (
          <Alert type="danger" message={formError} className="mb-3" />
        )}

        <div className="row g-3">
          {/* Nombre */}
          <div className="col-md-8">
            <label htmlFor="nombre" className="form-label">
              Nombre <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <Icon icon="mdi:school-outline" width="20" height="20" />
              </span>
              <input
                type="text"
                className={`form-control ${errors.nombre ? "is-invalid" : ""}`}
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Ej: Universidad Nacional Autónoma de México"
                disabled={formLoading}
              />
              {errors.nombre && (
                <div className="invalid-feedback">{errors.nombre}</div>
              )}
            </div>
          </div>

          {/* Siglas */}
          <div className="col-md-4">
            <label htmlFor="siglas" className="form-label">
              Siglas <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <Icon icon="mdi:alphabetical" width="20" height="20" />
              </span>
              <input
                type="text"
                className={`form-control ${errors.siglas ? "is-invalid" : ""}`}
                id="siglas"
                name="siglas"
                value={formData.siglas}
                onChange={handleChange}
                placeholder="Ej: UNAM"
                disabled={formLoading}
              />
              {errors.siglas && (
                <div className="invalid-feedback">{errors.siglas}</div>
              )}
            </div>
          </div>

          {/* Tipo */}
          <div className="col-md-8">
            <label htmlFor="tipo" className="form-label">
              Tipo <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <Icon icon="mdi:format-list-bulleted-type" width="20" height="20" />
              </span>
              <input
                type="text"
                className={`form-control ${errors.tipo ? "is-invalid" : ""}`}
                id="tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                placeholder="Ej: Universidad"
                disabled={formLoading}
              />
              {errors.tipo && (
                <div className="invalid-feedback">{errors.tipo}</div>
              )}
            </div>
          </div>

          {/* Pública */}
          <div className="col-md-4 d-flex align-items-end pb-1">
            <div className="form-check form-switch ms-1">
              <input
                type="checkbox"
                name="publica"
                className="form-check-input"
                id="publica"
                checked={formData.publica}
                onChange={handleChange}
                disabled={formLoading}
              />
              <label className="form-check-label fw-medium" htmlFor="publica">
                ¿Es pública?
              </label>
            </div>
          </div>

          {/* Para público en general */}
          <div className="col-md-4 d-flex align-items-end pb-1">
            <div className="form-check form-switch ms-1">
              <input
                type="checkbox"
                name="para_publico_en_general"
                className="form-check-input"
                id="para_publico_en_general"
                checked={formData.para_publico_en_general}
                onChange={handleChange}
                disabled={formLoading}
              />
              <label className="form-check-label fw-medium" htmlFor="para_publico_en_general">
                ¿Para público en general?
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={formLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary d-flex align-items-center gap-2"
            disabled={formLoading}
          >
            {formLoading ? (
              <>
                <Loader type="spinner" size="sm" delay={0} />
                Guardando...
              </>
            ) : (
              <>
                <Icon icon="mdi:content-save" width="20" height="20" />
                {formMode === "create" ? "Crear" : "Actualizar"}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SchoolFormModal;
